/** Typed fetch wrapper for /api. Throws ApiError for non-2xx responses. */
export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public body: Record<string, unknown> = {},
  ) {
    super(message);
  }
}

/**
 * Fired on `window` when an /api call (other than /auth/*) shows the session has ended:
 * any 401, a 403 "inactive", or a 403 on an admin route (the person is no longer an admin).
 * SessionProvider listens and re-checks /me, so the sign-in screen (or the cleaner view)
 * replaces pages that would otherwise keep failing.
 */
export const SESSION_EXPIRED_EVENT = "session-expired";

export interface SessionExpiredDetail {
  path: string;
  status: number;
}

/** True when a failed response means the signed-in session is gone or no longer allowed. */
export function endsSession(path: string, status: number, code: string): boolean {
  if (path.startsWith("/auth/")) return false;
  if (status === 401) return true;
  if (status !== 403) return false;
  return code === "inactive" || (code === "forbidden" && path.startsWith("/admin"));
}

/** Field problems the server listed for a 422 invalid_input / invalid_query, as one line. */
function issuesText(d: Record<string, unknown>): string {
  if (d.error !== "invalid_input" && d.error !== "invalid_query") return "";
  if (!Array.isArray(d.issues)) return "";
  const parts = (d.issues as { path?: unknown; message?: unknown }[])
    .slice(0, 3)
    .map((i) => {
      const field = typeof i.path === "string" && i.path ? i.path : "";
      const msg = typeof i.message === "string" ? i.message : "";
      return field && msg ? `${field}: ${msg}` : field || msg;
    })
    .filter(Boolean);
  return parts.length ? ` (${parts.join("; ")})` : "";
}

export async function api<T>(path: string, init: RequestInit & { json?: unknown } = {}): Promise<T> {
  const headers = new Headers(init.headers);
  let body = init.body;
  if (init.json !== undefined) {
    headers.set("Content-Type", "application/json");
    body = JSON.stringify(init.json);
  }
  let res: Response;
  try {
    res = await fetch(`/api${path}`, { ...init, headers, body, credentials: "same-origin" });
  } catch {
    throw new ApiError(0, "network", "No connection. Check your signal and try again.");
  }
  const text = await res.text();
  let data: unknown = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = text;
  }
  if (!res.ok) {
    const d = (data && typeof data === "object" ? data : {}) as Record<string, unknown>;
    const code = String(d.error ?? "error");
    if (endsSession(path, res.status, code) && typeof window !== "undefined") {
      const detail: SessionExpiredDetail = { path, status: res.status };
      window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT, { detail }));
    }
    throw new ApiError(
      res.status,
      code,
      `${String(d.message ?? `Request failed (${res.status})`)}${issuesText(d)}`,
      d,
    );
  }
  return data as T;
}

export const get = <T>(path: string) => api<T>(path);
export const post = <T>(path: string, json?: unknown) => api<T>(path, { method: "POST", json: json ?? {} });
export const patch = <T>(path: string, json: unknown) => api<T>(path, { method: "PATCH", json });
export const put = <T>(path: string, json: unknown) => api<T>(path, { method: "PUT", json });
export const del = <T>(path: string, json?: unknown) => api<T>(path, { method: "DELETE", json });
